/**
 * Quota Store -- Zustand (ephemeral, no persistence).
 *
 * Holds derived quota state for quick access by components.
 * Fed by useMyQuotas hook and SSE events.
 *
 * State:
 *   - quotas: TokenUsage[] from the API
 *   - envelope / mostRestrictive: unified envelope (FEAT-036); pct is 0..100
 *   - isWarning: at least one quota (or the most restrictive bar) is 80-94%
 *   - isCritical: 95-99%
 *   - isExceeded: >= 100%
 *   - exceededBanner: i18n descriptor to show when a quota/pool is exhausted during streaming
 */

import { create } from 'zustand';
import type { QuotaEnvelope, QuotaEnvelopeBar, TokenUsage } from '@/services/api/quotas';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** i18n descriptor (never a fixed text): the component formats it with intl.formatMessage. */
export interface ExceededBannerDescriptor {
  messageId: string;
  values?: Record<string, string | number>;
}

/** FEAT-054: the user entered the grace margin (SSE quota_warning level 'grace'); a toast shows it once. */
export interface GraceNotice {
  /** Tokens left until the block point, when the event carried it */
  remaining?: number;
}

interface QuotaState {
  quotas: TokenUsage[];
  envelope: QuotaEnvelope | null;
  mostRestrictive: QuotaEnvelopeBar | null;
  isWarning: boolean;
  isCritical: boolean;
  isExceeded: boolean;
  exceededBanner: ExceededBannerDescriptor | null;
  /** Pending grace toast (consumed by useQuotaGraceToast) */
  graceNotice: GraceNotice | null;
  /** Period (reset_at, or 'session') of the last grace toast: one toast per period */
  graceToastedFor: string | null;

  // Actions
  setQuotas: (quotas: TokenUsage[]) => void;
  setEnvelope: (envelope: QuotaEnvelope | null) => void;
  setExceededBanner: (banner: ExceededBannerDescriptor | null) => void;
  setGraceNotice: (notice: GraceNotice | null) => void;
  markGraceToasted: (key: string) => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function deriveFlags(quotas: TokenUsage[], mostRestrictive: QuotaEnvelopeBar | null) {
  let isWarning = false;
  let isCritical = false;
  let isExceeded = false;

  const percentages = quotas.map((q) => q.usage_percentage * 100);
  // Envelope pct is already 0..100 (FE-10: never multiplied again).
  if (mostRestrictive) percentages.push(mostRestrictive.pct);

  for (const pct of percentages) {
    if (pct >= 100) isExceeded = true;
    else if (pct >= 95) isCritical = true;
    else if (pct >= 80) isWarning = true;
  }

  return { isWarning, isCritical, isExceeded };
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export const useQuotaStore = create<QuotaState>((set, get) => ({
  quotas: [],
  envelope: null,
  mostRestrictive: null,
  isWarning: false,
  isCritical: false,
  isExceeded: false,
  exceededBanner: null,
  graceNotice: null,
  graceToastedFor: null,

  setQuotas: (quotas) => {
    const flags = deriveFlags(quotas, get().mostRestrictive);
    set({ quotas, ...flags });
  },

  setEnvelope: (envelope) => {
    const mostRestrictive = envelope?.most_restrictive ?? null;
    const flags = deriveFlags(get().quotas, mostRestrictive);
    set({ envelope, mostRestrictive, ...flags });
  },

  setExceededBanner: (banner) => set({ exceededBanner: banner }),
  setGraceNotice: (notice) => set({ graceNotice: notice }),
  markGraceToasted: (key) => set({ graceToastedFor: key, graceNotice: null }),
}));
